import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { IconAudit } from '../components/icons';
import { Avatar, Chips, DataTable, Empty, ErrorNote, Loading, PageHead, Pager, RowCard } from '../components/ui';
import { api } from '../lib/api';
import { dateTime, shortId } from '../lib/format';
import { useStaffNames } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import type { AuditEntry, Page } from '../lib/types';
import { auditRoute } from '../router';

const ACTION_LABEL: Record<string, string> = {
  'admin.login': 'Signed in',
  'driver.approve': 'Approved a driver',
  'driver.reject': 'Rejected a driver',
  'driver.suspend': 'Suspended a driver',
  'rider.block': 'Blocked a rider',
  'rider.unblock': 'Unblocked a rider',
  'ride.cancel': 'Cancelled a ride',
  'fare.publish': 'Published a fare',
  'settings.update': 'Changed settings',
  'sos.acknowledge': 'Took an SOS',
  'sos.resolve': 'Resolved an SOS',
  'staff.create': 'Added staff',
  'staff.update': 'Changed staff',
  'staff.password': 'Changed password',
  'commission.create': 'Added a commission rule',
  'commission.delete': 'Removed a commission rule',
  'payment.mark-paid': 'Marked a ride as paid',
  'payment.refund': 'Refunded a payment',
  'milestone.create': 'Added a milestone',
  'milestone.update': 'Changed a milestone',
  'milestone.delete': 'Removed a milestone',
  'milestone.deactivate': 'Switched off a milestone',
  'reward.void': 'Withdrew a reward',
  'driver.settle': 'Recorded cash with a driver',
  'payout.create': 'Created a payout',
  'payout.mark-paid': 'Marked a payout paid',
  'payout.cancel': 'Cancelled a payout',
  'ledger.adjust': 'Adjusted a driver’s balance',
};

const IGNORED = new Set(['updatedAt', 'createdAt', 'passwordHash', 'id']);

/** Only the fields that changed: [field, before, after]. */
export function changes(before: unknown, after: unknown): [string, unknown, unknown][] {
  const b = (before && typeof before === 'object' ? before : {}) as Record<string, unknown>;
  const a = (after && typeof after === 'object' ? after : {}) as Record<string, unknown>;
  const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const out: [string, unknown, unknown][] = [];
  for (const k of keys) {
    if (IGNORED.has(k)) continue;
    if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) out.push([k, b[k], a[k]]);
  }
  return out;
}

const show = (v: unknown) => (v === undefined || v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

function EntityLink({ e }: { e: AuditEntry }) {
  const label = `${e.entityType} #${shortId(e.entityId)}`;
  if (e.entityType === 'Ride') return <Link to="/rides/$rideId" params={{ rideId: e.entityId }} className="link">{label}</Link>;
  if (e.entityType === 'DriverPayout' || e.entityType === 'Payout') return <Link to="/payouts" search={{ tab: 'payouts', status: 'ALL' }} className="link">Payout #{shortId(e.entityId)}</Link>;
  if (e.entityType === 'Driver') return <Link to="/drivers/$driverId" params={{ driverId: e.entityId }} className="link">{label}</Link>;
  if (e.entityType === 'Rider') return <Link to="/riders/$riderId" params={{ riderId: e.entityId }} className="link">{label}</Link>;
  if (e.entityType === 'PlatformSettings') return <Link to="/settings" className="link">Settings</Link>;
  if (e.entityType === 'FareConfig') return <Link to="/fares" className="link">Fares</Link>;
  if (e.entityType === 'CommissionRule') return <Link to="/commission" className="link">Commission</Link>;
  if (e.entityType === 'Milestone' || e.entityType === 'EarnedReward') return <Link to="/rewards" className="link">Rewards</Link>;
  if (e.entityType === 'SosEvent') return <Link to="/sos" search={{ status: 'RESOLVED' }} className="link">SOS #{shortId(e.entityId)}</Link>;
  return <span>{label}</span>;
}

const TYPES = [
  { value: 'ALL', label: 'Everything' },
  { value: 'Driver', label: 'Drivers' },
  { value: 'Ride', label: 'Rides' },
  { value: 'Rider', label: 'Riders' },
  { value: 'FareConfig', label: 'Fares' },
  { value: 'CommissionRule', label: 'Commission' },
  { value: 'Milestone', label: 'Rewards' },
  { value: 'Payout', label: 'Payouts' },
  { value: 'PlatformSettings', label: 'Settings' },
  { value: 'SosEvent', label: 'SOS' },
  { value: 'AdminUser', label: 'Staff' },
];

export function AuditPage() {
  const search = auditRoute.useSearch();
  const nav = auditRoute.useNavigate();
  const staffName = useStaffNames();
  const [open, setOpen] = useState<string | null>(null);
  const page = search.page ?? 1;
  const q = useQuery({
    queryKey: [...qk.audit, search],
    queryFn: () => api.get<Page<AuditEntry>>('/admin/audit', { entityType: search.entityType, page, pageSize: 30 }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHead title="Audit log" sub="Every change made by staff: who, what, and when." />
      <div className="toolbar">
        <Chips
          label="Filter"
          value={search.entityType ?? 'ALL'}
          options={TYPES}
          onChange={(v) => nav({ search: { entityType: v === 'ALL' ? undefined : v } })}
        />
      </div>
      <section className="card">
        {q.isLoading ? (
          <Loading rows={6} />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title="Nothing recorded yet" icon={IconAudit} />
        ) : (
          <>
            <DataTable
              label="Audit log"
              rows={q.data.items}
              rowKey={(e) => e.id}
              columns={[
                { key: 'when', header: 'When', className: 'nowrap', cell: (e) => <span className="primary">{dateTime(e.createdAt)}</span> },
                {
                  key: 'who',
                  header: 'Who',
                  cell: (e) => (
                    <span className="person">
                      <Avatar name={staffName(e.adminId)} />
                      {staffName(e.adminId)}
                    </span>
                  ),
                },
                { key: 'what', header: 'What', cell: (e) => ACTION_LABEL[e.action] ?? e.action },
                { key: 'on', header: 'On', cell: (e) => <EntityLink e={e} /> },
                { key: 'diff', header: '', className: 'right', cell: (e) => <DiffButton e={e} open={open === e.id} onToggle={() => setOpen(open === e.id ? null : e.id)} /> },
              ]}
              card={(e) => (
                <RowCard
                  lead={<Avatar name={staffName(e.adminId)} />}
                  title={ACTION_LABEL[e.action] ?? e.action}
                  sub={`${staffName(e.adminId)} · ${dateTime(e.createdAt)}`}
                  side={<EntityLink e={e} />}
                />
              )}
              phoneExtra={(e) => (changes(e.before, e.after).length ? <DiffButton e={e} open={open === e.id} onToggle={() => setOpen(open === e.id ? null : e.id)} /> : null)}
              expanded={(e) => (open === e.id ? <Diff e={e} /> : null)}
            />
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
    </>
  );
}

function DiffButton({ e, open, onToggle }: { e: AuditEntry; open: boolean; onToggle: () => void }) {
  const n = changes(e.before, e.after).length;
  if (!n) return null;
  return (
    <button className="btn btn-sm btn-ghost" aria-expanded={open} onClick={onToggle}>
      {open ? 'Hide' : `${n} change${n === 1 ? '' : 's'}`}
    </button>
  );
}

function Diff({ e }: { e: AuditEntry }) {
  return (
    <div className="stack-xs small">
      {changes(e.before, e.after).map(([k, b, a]) => (
        <div key={k} style={{ overflowWrap: 'anywhere' }}>
          <span className="muted">{k}:</span> <s className="faint">{show(b)}</s> → <strong>{show(a)}</strong>
        </div>
      ))}
    </div>
  );
}
